/**
 * SPRINT-035: Main checkout hook.
 *
 * Wires together: state machine, API client, polling, redirect handling.
 *
 * Design notes:
 * - Each `useEffect` handles exactly one state transition (single responsibility)
 * - SUCCESS_VERIFYING ALWAYS re-fetches the session — never trusts stale data
 * - Polling uses wall-clock time to detect timeout (not attempt counter)
 * - Client secret read once on mount; API instance lives in a ref
 * - ATTEMPT_IN_PROGRESS → reload session to recover existing attempt state
 */

import { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { checkoutReducer, POLL_INTERVAL_MS, POLL_INTERVAL_MS_DEGRADED, POLL_TIMEOUT_MS } from '@/lib/state-machine';
import type { CardTokenResult } from '@/lib/state-machine';
import { CheckoutAPI } from '@/lib/api';
import { getClientSecret, clearClientSecret, storeRedirectInfo } from '@/lib/secret';
import { pickChannelType } from '@/lib/types';
import type { PaymentOption } from '@/lib/types';
import { checkoutTelemetry } from '@/lib/telemetry';
import { categorizeState } from '@/lib/runtime-state-adapter';
import { useCheckoutRuntimeStream } from './useCheckoutRuntimeStream';
import { useCheckoutRecovery } from './useCheckoutRecovery';

export function useCheckout(sessionId: string) {
  const [state, dispatch] = useReducer(checkoutReducer, { status: 'INIT' });
  const apiRef = useRef<CheckoutAPI | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paymentOptionsRef = useRef<PaymentOption[]>([]);
  const startTimeRef = useRef<number>(Date.now());
  // [PR-14.25] Card token ref — holds token result between CARD_TOKENIZED dispatch and createAttempt call.
  // [INVARIANT 13] Token result contains only safe metadata (id, last4, brand) — never PAN/CVV.
  const cardTokenRef = useRef<CardTokenResult | null>(null);
  // PR-CHECKOUT-LIVE-UPDATES-V1
  const [secret, setSecret] = useState<string | null>(null);
  const [sseConnected, setSseConnected] = useState(false);
  // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: ref tracks current state for recovery callback (avoids stale closure)
  const stateRef = useRef(state);

  // ---------------------------------------------------------------------------
  // Initialize: read secret, create API instance, trigger first load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!sessionId || apiRef.current) return;

    const secret = getClientSecret(sessionId);
    if (!secret) {
      dispatch({
        type: 'SESSION_LOAD_ERROR',
        error: {
          code: 'NO_SECRET',
          message: 'Authentication missing. Please restart checkout from the merchant site.',
          recoverable: false,
        },
      });
      return;
    }

    setSecret(secret);
    apiRef.current = new CheckoutAPI(secret);
    dispatch({ type: 'LOAD_SESSION' });
  }, [sessionId]);

  // Keep stateRef current so recovery callback always reads latest state
  useEffect(() => { stateRef.current = state; });

  // ---------------------------------------------------------------------------
  // PR-CHECKOUT-LIVE-UPDATES-V1: SSE runtime-state stream
  // RULE 1: snapshots are delivery only — no orchestration semantics invented here.
  // RULE 2: disconnect is NOT failure — polling degrades gracefully to normal cadence.
  // ---------------------------------------------------------------------------
  useCheckoutRuntimeStream({
    sessionId,
    secret: secret ?? '',
    onSnapshot: (snapshot) => {
      setSseConnected(true);
      const category = categorizeState(snapshot.runtimeState, snapshot.status);
      if (category === 'terminal_success') {
        dispatch({ type: 'POLL_COMPLETED' });
      } else if (category === 'terminal_expired') {
        dispatch({ type: 'POLL_FAILED', error: { code: 'SESSION_EXPIRED', message: 'Session expired.', recoverable: false } });
      } else if (category === 'terminal_cancelled') {
        dispatch({ type: 'POLL_FAILED', error: { code: 'SESSION_CANCELLED', message: 'Session was cancelled.', recoverable: false } });
      } else if (category === 'terminal_failed') {
        dispatch({ type: 'POLL_FAILED', error: { code: 'PAYMENT_FAILED', message: 'Payment failed.', recoverable: true } });
      }
      // Non-terminal: SSE noted as connected — polling degrades to POLL_INTERVAL_MS_DEGRADED
    },
    onDisconnect: () => {
      // RULE 2: SSE disconnect must NOT dispatch FAILURE — polling resumes at normal cadence
      setSseConnected(false);
    },
  });

  // ---------------------------------------------------------------------------
  // Load session + payment options
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.status !== 'LOADING_SESSION' || !apiRef.current) return;
    const api = apiRef.current;

    let cancelled = false;

    (async () => {
      try {
        const [session, { options, savedMethodOneTap }] = await Promise.all([
          api.fetchSession(sessionId),
          api.fetchPaymentOptions(sessionId),
        ]);
        if (cancelled) return;
        paymentOptionsRef.current = options;
        dispatch({ type: 'SESSION_LOADED', session, paymentOptions: options, savedMethodOneTap });
        checkoutTelemetry.sessionLoaded(sessionId);
      } catch (err: unknown) {
        if (cancelled) return;
        dispatch({
          type: 'SESSION_LOAD_ERROR',
          error: isCheckoutError(err)
            ? err
            : { code: 'LOAD_ERROR', message: 'Failed to load checkout.', recoverable: false },
        });
      }
    })();

    return () => { cancelled = true; };
  }, [state.status, sessionId]);

  // ---------------------------------------------------------------------------
  // PR-CHECKOUT-ASYNC-RECOVERY-UX-V1: visibilitychange + online recovery
  //
  // RULE 1: backend state is authoritative — browser lifecycle never transitions state.
  // RULE 4: recovery dispatches LOAD_SESSION → authoritative fetch → canonical re-render.
  //
  // Only triggers from states where the backend may have advanced while the tab
  // was backgrounded or the device was offline.
  // ---------------------------------------------------------------------------
  const RESUMABLE_STATES = new Set([
    'POLLING', 'QR_DISPLAY', 'REFERENCE_DISPLAY', 'AWAITING_CONFIRMATION', 'STILL_PROCESSING', 'STRIPE_CONFIRM', 'PAYZEN_FORM',
  ]);

  useCheckoutRecovery({
    onRecover: useCallback(() => {
      if (RESUMABLE_STATES.has(stateRef.current.status)) {
        dispatch({ type: 'LOAD_SESSION' });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]),
  });

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if ((state.status !== 'POLLING' && state.status !== 'REFERENCE_DISPLAY' && state.status !== 'QR_DISPLAY' && state.status !== 'AWAITING_CONFIRMATION') || !apiRef.current) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }

    const api = apiRef.current;
    const { pollStartedAt } = state;

    const tick = async () => {
      if (Date.now() - pollStartedAt >= POLL_TIMEOUT_MS) {
        checkoutTelemetry.pollTimeout(sessionId);
        // ADR-098 / CRS-005: Poll timeout is NOT a failure.
        // Dispatch POLL_TIMEOUT → STILL_PROCESSING (not FAILURE).
        dispatch({ type: 'POLL_TIMEOUT' });
        return;
      }

      try {
        // Poll the session (authoritative — reflects reconcileFromPayment).
        // ADR-098: canonical state interpretation delegated to runtime-state-adapter.
        const session = await api.fetchSession(sessionId);
        const stateCategory = categorizeState(session.runtime_state, session.status);

        if (stateCategory === 'terminal_success') {
          dispatch({ type: 'POLL_COMPLETED' });
        } else if (stateCategory === 'terminal_expired') {
          dispatch({
            type: 'POLL_FAILED',
            error: { code: 'SESSION_EXPIRED', message: 'Session expired.', recoverable: false },
          });
        } else if (stateCategory === 'terminal_cancelled') {
          dispatch({
            type: 'POLL_FAILED',
            error: { code: 'SESSION_CANCELLED', message: 'Session was cancelled.', recoverable: false },
          });
        } else if (stateCategory === 'terminal_failed') {
          // Provider-level failure (not browser timeout) — allow retry
          dispatch({
            type: 'POLL_FAILED',
            error: { code: 'PAYMENT_FAILED', message: 'Payment failed.', recoverable: true },
          });
        }
        // in_progress, awaiting_customer_action, awaiting_confirmation → keep polling
      } catch (err: unknown) {
        // Network errors during polling are non-fatal — keep trying
        console.warn('[checkout] poll error (will retry):', err);
      }
    };

    // Run immediately, then on interval.
    // PR-CHECKOUT-LIVE-UPDATES-V1: degrade to 30s when SSE is connected (backend pushes updates).
    const interval = sseConnected ? POLL_INTERVAL_MS_DEGRADED : POLL_INTERVAL_MS;
    tick();
    pollTimerRef.current = setInterval(tick, interval);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, sessionId, sseConnected]);

  // ---------------------------------------------------------------------------
  // SUCCESS_VERIFYING: re-fetch to confirm and get fresh session (with retry)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.status !== 'SUCCESS_VERIFYING' || !apiRef.current) return;
    const api = apiRef.current;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    const verify = async () => {
      if (cancelled) return;

      // Give up after POLL_TIMEOUT_MS
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        dispatch({ type: 'SESSION_VERIFIED_NOT_COMPLETE' });
        return;
      }

      try {
        // INVARIANT: Do NOT use stale session — always re-fetch
        const freshSession = await api.fetchSession(sessionId);
        if (cancelled) return;

        if (categorizeState(freshSession.runtime_state, freshSession.status) === 'terminal_success') {
          dispatch({ type: 'SESSION_VERIFIED_COMPLETE', session: freshSession });
        } else {
          // Session not yet reconciled — retry after 1.5s
          retryTimer = setTimeout(verify, 1500);
        }
      } catch {
        if (!cancelled) retryTimer = setTimeout(verify, 1500);
      }
    };

    verify();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [state.status, sessionId]);

  // ---------------------------------------------------------------------------
  // REDIRECTING: store info for recovery, then navigate
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.status !== 'REDIRECTING') return;
    const { session, redirectUrl } = state;

    if (session.current_attempt_id) {
      storeRedirectInfo(sessionId, session.current_attempt_id, redirectUrl);
    }

    checkoutTelemetry.redirectStarted(sessionId, '');
    // setTimeout + cleanup prevents React StrictMode from double-navigating
    // (StrictMode runs effects twice in dev; cleanup cancels the first timer)
    const timer = setTimeout(() => { window.location.href = redirectUrl; }, 0);
    return () => clearTimeout(timer);
  }, [state.status, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // SUCCESS: clear secret, redirect to merchant success_url (if set)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (state.status !== 'SUCCESS') return;
    const { session } = state;

    checkoutTelemetry.completed(sessionId, Date.now() - startTimeRef.current);
    clearClientSecret(sessionId);

    // success_url may be empty for renewal payments (no merchant storefront to return to)
    if (!session.success_url) return;
    try {
      const url = new URL(session.success_url);
      url.searchParams.set('session_id', session.id);
      window.location.href = url.toString();
    } catch {
      // invalid URL — stay on success screen
    }
  }, [state.status, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  // [PR-14.25] When PROCESSING starts with a pending card token, fire createAttempt with the token.
  // This effect fires once after CARD_TOKENIZED → PROCESSING transition.
  useEffect(() => {
    const token = cardTokenRef.current;
    if (state.status !== 'PROCESSING' || !token || !apiRef.current) return;

    // Consume the token — [INVARIANT 16] token is single-use per attempt
    cardTokenRef.current = null;

    const api = apiRef.current;

    (async () => {
      try {
        const result = await api.createAttempt(sessionId, 'card', 'redirect', {
          paymentMethod: {
            type: 'card',
            payment_method_id: token.payment_method_id,
            ...(token.last4 ? { last4: token.last4 } : {}),
            ...(token.brand ? { brand: token.brand } : {}),
          },
        });
        checkoutTelemetry.attemptCreated(sessionId, result.attempt_id, 'card');
        dispatch({ type: 'ATTEMPT_CREATED', attempt: result });
      } catch (err: unknown) {
        if (isCheckoutError(err) && err.code === 'ATTEMPT_IN_PROGRESS') {
          dispatch({ type: 'ATTEMPT_IN_PROGRESS' });
        } else {
          checkoutTelemetry.attemptFailed(sessionId, undefined, isCheckoutError(err) ? err.code : 'CARD_ATTEMPT_FAILED');
          dispatch({
            type: 'ATTEMPT_FAILED',
            error: isCheckoutError(err)
              ? err
              : { code: 'CARD_ATTEMPT_FAILED', message: 'El pago con tarjeta no pudo iniciarse. Por favor intenta de nuevo.', recoverable: true },
          });
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status, sessionId]);

  const _callCreateAttempt = useCallback(
    async (productKey: string, metadata?: Record<string, unknown>, customer?: Record<string, unknown>, channelTypeOverride?: string, paymentMethod?: Record<string, unknown>) => {
      if (!apiRef.current) return;
      const option = paymentOptionsRef.current.find((o) => o.methodKey === productKey);
      const channelType = channelTypeOverride ?? (option ? pickChannelType(option) : 'embedded');

      try {
        const result = await apiRef.current.createAttempt(sessionId, productKey, channelType, {
          ...(metadata ? { metadata } : {}),
          ...(customer ? { customer } : {}),
          ...(paymentMethod ? { paymentMethod } : {}),
        });

        if (result.next_action.type === 'redirect') {
          const url = result.next_action.data?.redirect_url ?? result.next_action.data?.url;
          if (url) storeRedirectInfo(sessionId, result.attempt_id, url as string);
        }

        checkoutTelemetry.attemptCreated(sessionId, result.attempt_id, productKey);
        dispatch({ type: 'ATTEMPT_CREATED', attempt: result });
      } catch (err: unknown) {
        if (isCheckoutError(err) && err.code === 'ATTEMPT_IN_PROGRESS') {
          dispatch({ type: 'ATTEMPT_IN_PROGRESS' });
        } else {
          checkoutTelemetry.attemptFailed(sessionId, undefined, isCheckoutError(err) ? err.code : 'ATTEMPT_FAILED');
          dispatch({
            type: 'ATTEMPT_FAILED',
            error: isCheckoutError(err)
              ? err
              : { code: 'ATTEMPT_FAILED', message: 'Payment failed to start.', recoverable: true },
          });
        }
      }
    },
    [sessionId],
  );

  const selectMethod = useCallback(
    async (productKey: string, channelType?: string) => {
      if (!apiRef.current) return;

      checkoutTelemetry.methodSelected(sessionId, productKey);
      dispatch({ type: 'SELECT_METHOD', productKey });
      if (productKey === 'platform_wallet') return; // WALLET_CONFIRM state handles next step

      // If method has requiredFields, state machine goes to COLLECTING_FIELDS — skip attempt creation
      const option = paymentOptionsRef.current.find((o) => o.methodKey === productKey);
      if (option?.requiredFields && option.requiredFields.length > 0) return;
      // If method requires tokenization, state machine goes to TOKENIZE_CARD — skip attempt creation
      if (option?.tokenizationConfig) return;

      await _callCreateAttempt(productKey, undefined, undefined, channelType);
    },
    [sessionId, _callCreateAttempt],
  );

  const submitFields = useCallback(
    async (productKey: string, values: Record<string, string>) => {
      if (!apiRef.current) return;
      dispatch({ type: 'FIELDS_SUBMITTED' });

      // Split fields: customer_* prefix → customer object; rest → metadata
      // Fields prefixed with customer_ map to customer.{name} (e.g. customer_name → customer.name)
      const metadata: Record<string, unknown> = {};
      const customer: Record<string, unknown> = {};

      for (const [key, val] of Object.entries(values)) {
        if (key.startsWith('customer_')) {
          customer[key.slice('customer_'.length)] = val;
        } else if (key === 'user_type') {
          // PSE: user_type must be numeric (0=natural, 1=juridical)
          metadata[key] = parseInt(val, 10);
        } else {
          metadata[key] = val;
        }
      }

      await _callCreateAttempt(
        productKey,
        Object.keys(metadata).length > 0 ? metadata : undefined,
        Object.keys(customer).length > 0 ? customer : undefined,
      );
    },
    [_callCreateAttempt],
  );

  // [PR-14.25] Card tokenization actions
  const cardTokenizationSuccess = useCallback((token: CardTokenResult) => {
    // [INVARIANT 13] Store ONLY the token result — never PAN/CVV — in the ref
    cardTokenRef.current = token;
    // State machine → PROCESSING; the PROCESSING useEffect above fires createAttempt
    dispatch({ type: 'CARD_TOKENIZED', token });
  }, []);

  const cardTokenizationError = useCallback((_safeMessage: string) => {
    // State machine stays in TOKENIZE_CARD; component re-renders showing the error
    dispatch({ type: 'CARD_TOKENIZATION_FAILED', error: _safeMessage });
  }, []);

  const cardTokenizationCancel = useCallback(() => {
    dispatch({ type: 'BACK_TO_METHODS' });
  }, []);

  const backToMethods = useCallback(() => dispatch({ type: 'BACK_TO_METHODS' }), []);

  const confirmWallet = useCallback(async () => {
    if (!apiRef.current) return;

    dispatch({ type: 'CONFIRM_WALLET' });

    try {
      const result = await apiRef.current.createAttempt(
        sessionId,
        'platform_wallet',
        'embedded',
        { walletConfirm: true },
      );
      checkoutTelemetry.attemptCreated(sessionId, result.attempt_id, 'platform_wallet');
      dispatch({ type: 'ATTEMPT_CREATED', attempt: result });
    } catch (err: unknown) {
      if (isCheckoutError(err) && err.code === 'ATTEMPT_IN_PROGRESS') {
        dispatch({ type: 'ATTEMPT_IN_PROGRESS' });
      } else {
        checkoutTelemetry.attemptFailed(sessionId, undefined, isCheckoutError(err) ? err.code : 'ATTEMPT_FAILED');
        dispatch({
          type: 'ATTEMPT_FAILED',
          error: isCheckoutError(err)
            ? err
            : { code: 'ATTEMPT_FAILED', message: 'Wallet payment failed.', recoverable: true },
        });
      }
    }
  }, [sessionId]);

  const selectSavedMethod = useCallback(
    async (productKey: string, paymentMethodId: string, channelType?: string) => {
      checkoutTelemetry.methodSelected(sessionId, productKey);
      dispatch({ type: 'SELECT_SAVED_METHOD', productKey, paymentMethodId });
      await _callCreateAttempt(productKey, undefined, undefined, channelType, {
        type: 'card',
        payment_method_id: paymentMethodId,
      });
    },
    [sessionId, _callCreateAttempt],
  );

  const cancelWallet = useCallback(() => dispatch({ type: 'CANCEL_WALLET' }), []);

  // ---------------------------------------------------------------------------
  // Stripe Express Checkout Element / Payment Element actions
  // Called by StripeExpressCheckout component after stripe.confirmPayment().
  // ---------------------------------------------------------------------------
  const stripeConfirmed = useCallback(() => dispatch({ type: 'STRIPE_CONFIRMED' }), []);
  const stripeConfirmFailed = useCallback((error: string) => dispatch({ type: 'STRIPE_CONFIRM_FAILED', error }), []);

  // PayZen SmartForm actions (PR-PAYZEN-SMARTFORM-CHECKOUT-UI-V1)
  // FINALITY GUARD: payzenFormCompleted → AWAITING_CONFIRMATION only.
  // Webhook/polling delivers actual finality — form completion is not payment confirmation.
  const payzenFormCompleted = useCallback(() => dispatch({ type: 'PAYZEN_FORM_COMPLETED' }), []);
  const payzenFormFailed = useCallback((error: string) => dispatch({ type: 'PAYZEN_FORM_FAILED', error }), []);

  const retry = useCallback(() => dispatch({ type: 'RETRY' }), []);

  // ADR-098: "check again" action for STILL_PROCESSING state.
  // Reloads canonical session state from backend — does not mark the session failed.
  const checkAgain = useCallback(() => dispatch({ type: 'LOAD_SESSION' }), []);

  const cancel = useCallback(async () => {
    if (apiRef.current && 'session' in state) {
      const { session } = state as { session: { status: string; cancel_url: string } };
      checkoutTelemetry.cancelled(sessionId);
      if (session.status === 'open' || session.status === 'payment_initiated') {
        try {
          await apiRef.current.cancelSession(sessionId);
        } catch {
          // Best-effort
        }
      }
      window.location.href = session.cancel_url;
    }
  }, [sessionId, state]);

  return {
    state,
    actions: {
      selectMethod,
      selectSavedMethod,
      submitFields,
      backToMethods,
      confirmWallet,
      cancelWallet,
      retry,
      cancel,
      checkAgain,
      cardTokenizationSuccess,
      cardTokenizationError,
      cardTokenizationCancel,
      stripeConfirmed,
      stripeConfirmFailed,
      payzenFormCompleted,
      payzenFormFailed,
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isCheckoutError(
  err: unknown,
): err is { code: string; message: string; recoverable: boolean } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    'message' in err &&
    'recoverable' in err
  );
}
